import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Sale } from '../../types';
import { Clock, Play, CheckCircle, Truck, X } from 'lucide-react';
import OrderDetailsModal from '../orders/OrderDetailsModal';
import { formatCurrency } from '../../utils/helpers';

interface DeliveryEvent {
  id: string;
  title: string;
  date: string;
  backgroundColor: string;
  borderColor: string;
  extendedProps: {
    saleId: string;
    customerName: string;
    totalAmount: number;
    amountPaid: number;
    remainingAmount: number;
    status: string;
    totalTrays: number;
  };
}

const Deliveries: React.FC = () => {
  const [deliveryEvents, setDeliveryEvents] = useState<DeliveryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<DeliveryEvent | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Sale | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    fetchDeliveryEvents();
  }, []);

  const fetchDeliveryEvents = async () => {
    try {
      setLoading(true);
      const salesRef = collection(db, 'sales');
      const q = query(salesRef, where('status', '!=', 'cancelled'));
      const querySnapshot = await getDocs(q);
      
      const events: DeliveryEvent[] = [];
      
      querySnapshot.forEach((doc) => {
        const sale = doc.data() as Sale;
        // Prefer expectedDeliveryDate; fall back to legacy deliveryDate if present
        const deliveryDate = (sale as any).expectedDeliveryDate as Timestamp || (sale as any).deliveryDate as Timestamp;
        
        if (deliveryDate) {
          const status = sale.status?.name?.toLowerCase() || 'pending';
          let backgroundColor = '#10B981'; // green for delivered
          let borderColor = '#059669';
          let textColor = '#ffffff';
          
          if (status === 'seedingpending') {
            backgroundColor = '#F97316'; // orange for seeding pending
            borderColor = '#EA580C';
          } else if (status === 'pending') {
            backgroundColor = '#EAB308'; // yellow for pending
            borderColor = '#CA8A04';
          } else if (status === 'in_progress' || status.includes('progress')) {
            backgroundColor = '#3B82F6'; // blue for in progress
            borderColor = '#2563EB';
          } else if (status === 'ready') {
            backgroundColor = '#8B5CF6'; // purple for ready
            borderColor = '#7C3AED';
          } else if (status === 'delivered') {
            backgroundColor = '#10B981'; // green for delivered
            borderColor = '#059669';
          } else if (status === 'complete') {
            backgroundColor = '#059669'; // dark green for complete
            borderColor = '#047857';
          } else if (status === 'cancelled') {
            backgroundColor = '#EF4444'; // red for cancelled
            borderColor = '#DC2626';
          }
          
          events.push({
            id: doc.id,
            title: `${sale.customerName || 'Customer'} - ${sale.totalTrays} trays`,
            date: deliveryDate.toDate().toISOString().split('T')[0],
            backgroundColor,
            borderColor,
            extendedProps: {
              saleId: doc.id,
              customerName: sale.customerName || 'Customer',
              totalAmount: sale.totalAmount,
              amountPaid: sale.amountPaid,
              remainingAmount: sale.remainingAmount,
              status,
              totalTrays: sale.totalTrays,
            },
          });
        }
      });
      
      setDeliveryEvents(events);
    } catch (error) {
      console.error('Error fetching delivery events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (clickInfo: any) => {
    setSelectedEvent(clickInfo.event);
  };

  const handleViewOrderDetails = async (saleId: string) => {
    try {
      // Fetch the order details from Firestore
      const salesRef = collection(db, 'sales');
      const q = query(salesRef, where('__name__', '==', saleId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const orderData = { id: doc.id, ...doc.data() } as Sale;
        setSelectedOrder(orderData);
        setShowOrderDetails(true);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'seedingpending':
        return 'text-orange-800 bg-orange-100 border border-orange-300';
      case 'pending':
        return 'text-yellow-800 bg-yellow-100 border border-yellow-300';
      case 'in_progress':
      case 'in progress':
        return 'text-blue-800 bg-blue-100 border border-blue-300';
      case 'ready':
        return 'text-purple-800 bg-purple-100 border border-purple-300';
      case 'delivered':
        return 'text-green-800 bg-green-100 border border-green-300';
      case 'complete':
        return 'text-emerald-800 bg-emerald-100 border border-emerald-300';
      case 'cancelled':
        return 'text-red-800 bg-red-100 border border-red-300';
      default:
        return 'text-gray-800 bg-gray-100 border border-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'seedingpending':
        return <Clock className="h-4 w-4 text-orange-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'in_progress':
      case 'in progress':
        return <Play className="h-4 w-4 text-blue-600" />;
      case 'ready':
        return <Truck className="h-4 w-4 text-purple-600" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'complete':
        return <CheckCircle className="h-4 w-4 text-emerald-600" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Deliveries Calendar</h1>
        <p className="text-orange-100 text-lg">View all scheduled deliveries and their status</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow">
              <div className="p-2 sm:p-6">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={deliveryEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,dayGridWeek'
                  }}
                  dayMaxEvents={true}
                  moreLinkClick="popover"
                  eventDisplay="block"
                  eventClassNames="cursor-pointer font-semibold text-white shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Event Details Sidebar */}
          <div className="lg:col-span-1">
            <div className={`rounded-lg shadow p-4 sm:p-6 transition-all duration-300 ${
              selectedEvent 
                ? selectedEvent.extendedProps.status === 'pending'
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500'
                  : selectedEvent.extendedProps.status === 'in_progress'
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                  : selectedEvent.extendedProps.status === 'ready'
                  ? 'bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500'
                  : selectedEvent.extendedProps.status === 'delivered'
                  ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500'
                  : 'bg-white dark:bg-gray-800'
                : 'bg-white dark:bg-gray-800'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${
                selectedEvent 
                  ? selectedEvent.extendedProps.status === 'pending'
                    ? 'text-amber-800 dark:text-amber-200'
                    : selectedEvent.extendedProps.status === 'in_progress'
                    ? 'text-blue-800 dark:text-blue-200'
                    : selectedEvent.extendedProps.status === 'ready'
                    ? 'text-purple-800 dark:text-purple-200'
                    : selectedEvent.extendedProps.status === 'delivered'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-gray-900 dark:text-gray-100'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>Delivery Details</h3>
              
              {selectedEvent ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Customer</h4>
                    <p className="text-gray-600 dark:text-gray-400">{selectedEvent.extendedProps.customerName}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Status</h4>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedEvent.extendedProps.status)}`}>
                      {getStatusIcon(selectedEvent.extendedProps.status)}
                      {selectedEvent.extendedProps.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Delivery Date</h4>
                    <p className="text-gray-600 dark:text-gray-400">{new Date(selectedEvent.date).toLocaleDateString('en-IN')}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">Order Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Total Trays:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedEvent.extendedProps.totalTrays}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(selectedEvent.extendedProps.totalAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Amount Paid:</span>
                        <span className="font-medium text-green-600 dark:text-green-400">{formatCurrency(selectedEvent.extendedProps.amountPaid)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Remaining:</span>
                        <span className="font-medium text-red-600 dark:text-red-400">{formatCurrency(selectedEvent.extendedProps.remainingAmount)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleViewOrderDetails(selectedEvent.extendedProps.saleId)}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                    >
                      View Order Details
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <div className="text-4xl mb-2">📅</div>
                  <p className="text-gray-600 dark:text-gray-400">Click on any delivery event to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Legend */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 transition-colors duration-200">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Status Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-orange-500 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Seedling Pending</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Pending</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">In Progress</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-purple-500 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Ready</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Delivered</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-emerald-600 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Complete</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Cancelled</span>
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={showOrderDetails}
        onClose={() => {
          setShowOrderDetails(false);
          setSelectedOrder(null);
        }}
        onOrderUpdate={(updatedOrder) => {
          // Update the delivery events if order status changes
          setDeliveryEvents(prevEvents => 
            prevEvents.map(event => 
              event.extendedProps.saleId === updatedOrder.id 
                ? {
                    ...event,
                    backgroundColor: updatedOrder.status.name.toLowerCase() === 'pending' ? '#F59E0B' :
                                  updatedOrder.status.name.toLowerCase() === 'in_progress' ? '#3B82F6' :
                                  updatedOrder.status.name.toLowerCase() === 'ready' ? '#8B5CF6' :
                                  updatedOrder.status.name.toLowerCase() === 'delivered' ? '#10B981' : '#10B981',
                    borderColor: updatedOrder.status.name.toLowerCase() === 'pending' ? '#D97706' :
                                updatedOrder.status.name.toLowerCase() === 'in_progress' ? '#2563EB' :
                                updatedOrder.status.name.toLowerCase() === 'ready' ? '#7C3AED' :
                                updatedOrder.status.name.toLowerCase() === 'delivered' ? '#059669' : '#059669',
                    extendedProps: {
                      ...event.extendedProps,
                      status: updatedOrder.status.name.toLowerCase()
                    }
                  }
                : event
            )
          );
        }}
      />
    </div>
  );
};

export default Deliveries; 